/**
 * AdminArchive.jsx — read-only browser for lbs_bookings_archive.
 *
 * Server-paginated (100 rows/page), filters go straight to Supabase
 * via the client-scoped `archive_read_tenant` RLS policy (migration
 * 102). No backend endpoint needed for reads.
 *
 * Ships 2026-07-14 alongside migration 102.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Archive, Search, RefreshCw, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { archiveStats, listArchive } from "@/lib/api/archive";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { AuDateInput } from "@/components/AuDateTime";

const PAGE_SIZE = 100;
const STATUSES = ["all", "completed", "cancelled", "rejected", "no_show", "approved", "archived"];
const DIRECTIONS = ["all", "inbound", "outbound"];
const LEGACY_SOURCES = ["all", "auto_archive", "csv_import", "booked_scheduler"];

const STATUS_TONE = {
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-700",
  rejected:  "bg-red-100 text-red-700",
  no_show:   "bg-amber-100 text-amber-800",
  approved:  "bg-blue-100 text-blue-700",
  archived:  "bg-slate-100 text-slate-700",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminArchive() {
  const { hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient() || {};

  const [filters, setFilters] = useState({
    dateFrom: "", dateTo: "", status: "all", direction: "all",
    driver: "", company: "", reference: "", legacySource: "all",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [migrationMissing, setMigrationMissing] = useState(false);

  const activeFilters = useMemo(() => ({
    clientId: viewAsClientId || null,
    ...filters,
    // Convert date inputs to ISO
    dateFrom: filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).toISOString() : "",
    dateTo:   filters.dateTo   ? new Date(`${filters.dateTo}T23:59:59`).toISOString()   : "",
  }), [filters, viewAsClientId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await listArchive(activeFilters, { limit: PAGE_SIZE, offset });
    setLoading(false);
    if (error) {
      const msg = String(error.message || "");
      if (/schema cache|not find the table|PGRST205|column .* does not exist/i.test(msg)) {
        setMigrationMissing(true);
        setRows([]); setTotal(0);
        return;
      }
      toast.error(msg);
      return;
    }
    setMigrationMissing(false);
    setRows(data || []);
    setTotal(count || 0);
  }, [activeFilters, offset]);

  const loadStats = useCallback(async () => {
    const { data, error } = await archiveStats({ clientId: viewAsClientId });
    if (error) return;
    setStats(data);
  }, [viewAsClientId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setOffset(0); };

  const downloadCsv = () => {
    if (!rows.length) { toast.info("Nothing to export in the current filter."); return; }
    const keys = [
      "reference_number","scheduled_start","scheduled_end","actual_start","actual_end",
      "status","direction","sender_company","receiver_company",
      "driver_name","driver_phone","driver_email","vehicle_type","vehicle_rego",
      "pallets_in","pallets_out","legacy_source","archived_at",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `archive-page-${Math.floor(offset / PAGE_SIZE) + 1}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!hasRole("super_admin") && !hasRole("client_admin") && !hasRole("site_admin")) {
    return <p className="text-sm text-rose-700">Admins only.</p>;
  }

  if (isSuperAdmin && !viewAsClientId) {
    return (
      <div className="p-6 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="admin-archive-gate">
        Pick a tenant via the top-nav <strong>View as client</strong> picker to browse their archive.
      </div>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-archive-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Archive className="w-6 h-6 text-slate-500" />
            Archive
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Historical bookings retained for 5 years. Anything older is
            cold-frozen to Supabase Storage nightly (see{" "}
            <code>POST /api/admin/archive/cold-freeze</code>). Read-only —
            corrections require a service-role SQL fix.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {stats && (
            <>
              <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                {stats.total?.toLocaleString?.() || 0} rows
              </Badge>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                {stats.last_12_mo?.toLocaleString?.() || 0} in last 12mo
              </Badge>
            </>
          )}
        </div>
      </div>

      {migrationMissing && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="archive-migration-missing">
          <strong>Migration 102 not applied yet.</strong> Ship{" "}
          <code className="mx-1">spec_review/100-features/102_bookings_archive_flat.sql</code>
          in the Supabase dashboard to enable the archive browser.
        </div>
      )}

      {/* ── Filter bar ── */}
      <Card>
        <CardContent className="pt-6 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-600">From</label>
            <AuDateInput value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} data-testid="archive-filter-from" />
          </div>
          <div>
            <label className="text-xs text-slate-600">To</label>
            <AuDateInput value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} data-testid="archive-filter-to" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Status</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={filters.status}
              onChange={(e) => setFilter("status", e.target.value)}
              data-testid="archive-filter-status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600">Direction</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={filters.direction}
              onChange={(e) => setFilter("direction", e.target.value)}
              data-testid="archive-filter-direction"
            >
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Driver name (fuzzy)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9" placeholder="Sam Driver…"
                value={filters.driver}
                onChange={(e) => setFilter("driver", e.target.value)}
                data-testid="archive-filter-driver"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600">Company (fuzzy)</label>
            <Input placeholder="Acme…"
              value={filters.company}
              onChange={(e) => setFilter("company", e.target.value)}
              data-testid="archive-filter-company"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Reference</label>
            <Input placeholder="BK-2022…"
              value={filters.reference}
              onChange={(e) => setFilter("reference", e.target.value)}
              data-testid="archive-filter-reference"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Source</label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={filters.legacySource}
              onChange={(e) => setFilter("legacySource", e.target.value)}
              data-testid="archive-filter-source"
            >
              {LEGACY_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="archive-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!rows.length} data-testid="archive-download-csv">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Results
            <span className="ml-2 text-xs font-normal text-slate-500">
              ({loading ? "…" : total.toLocaleString("en-AU", { hourCycle: "h23" })} matching, page {page} of {totalPages})
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              data-testid="archive-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              data-testid="archive-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dir.</TableHead>
                  <TableHead>Sender → Receiver</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-right">Pallets</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No archived bookings match the current filter.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`archive-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.reference_number || "—"}</TableCell>
                    <TableCell className="text-xs">
                      <div>{fmtDate(r.scheduled_start)}</div>
                      <div className="text-slate-400">{fmtDateTime(r.scheduled_start).split(", ")[1] || ""}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_TONE[r.status] || "bg-slate-100 text-slate-700"}>{r.status || "—"}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{r.direction || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[220px] truncate" title={`${r.sender_company || ""} → ${r.receiver_company || ""}`}>
                      {r.sender_company || "—"} → {r.receiver_company || "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.driver_name || "—"}</div>
                      <div className="text-slate-500">{r.driver_phone || ""}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{r.vehicle_type || "—"}</div>
                      <div className="text-slate-500 font-mono">{r.vehicle_rego || ""}</div>
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      <div>in {r.pallets_in ?? "—"}</div>
                      <div className="text-slate-500">out {r.pallets_out ?? "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="secondary" className="text-[10px]">
                        {r.legacy_source || "auto"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
